/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * Multi-Engine Image Generation Service
 * Unified interface for Nano Banana Pro, Flux, and LTX-2 image generation
 */
import { ImageFile, Character, Dogma } from '../types';
import { ShotSpec, ImageEngine, CharacterRef, composeShotPrompt } from '../types/decoupage';
import { generatePreview as generateNanoPreview } from './nanoService';
import { generateCharacterImage as generateWithNano } from './geminiService';
import { getModelById, ModelDefinition } from './modelRegistry';
import { uploadAssetToDrive } from './driveAssetManager';

// === TYPES ===

export interface ImageGenerationRequest {
    shot: ShotSpec;
    dogma: Dogma | null;
    characters: Character[];
    fps: number;
    aspectRatio: '16:9' | '9:16';
    referenceImage?: string; // used for variations or ratio adapt
    promptSuffix?: string;
}

export interface ImageGenerationResult {
    candidates: ImageFile[];
    engine: ImageEngine;
    seed?: number;
    error?: string;
}

export interface CharacterImageRefs {
    character_id: string;
    name: string;
    front?: ImageFile;
    profile?: ImageFile;
    back?: ImageFile;
}

// === CHARACTER IMAGE INJECTION ===

import { base64ToFile } from '../utils/base64';

// === TYPES ===
// ... existing types

/**
 * Resolve character references to actual image files
 * This is the KEY FIX for character persistence
 */
export function resolveCharacterImages(
    characterRefs: CharacterRef[] | undefined,
    characters: Character[]
): CharacterImageRefs[] {
    if (!characterRefs || characterRefs.length === 0) return [];

    const resolved: CharacterImageRefs[] = [];

    for (const ref of characterRefs) {
        const character = characters.find(c => c.id === ref.character_id);
        if (!character) {
            console.warn(`[ImageGenerators] Character not found: ${ref.name} (${ref.character_id})`);
            continue;
        }

        const refImages: CharacterImageRefs = {
            character_id: ref.character_id,
            name: ref.name,
        };

        // Find each requested reference image
        for (const refType of ref.refs) {
            // Priority: explicit 'view' property > name heuristic
            const img = character.images.find(i => {
                // If explicit view property exists (future proofing), use it
                if ((i as any).view === refType) return true;

                // Fallback to robust label heuristic
                const label = i.label.toLowerCase();
                const name = i.name.toLowerCase();
                const combined = `${label} ${name}`;

                switch (refType) {
                    case 'front':
                        return combined.includes('face') || combined.includes('front') || combined.includes('portr');
                    case 'profile':
                        return combined.includes('profil') || combined.includes('side');
                    case 'back':
                        return combined.includes('dos') || combined.includes('back') || combined.includes('rear');
                    default: return false;
                }
            });

            if (img) {
                // Convert CharacterImage to ImageFile using util to ensure real bytes
                const imageFile: ImageFile = {
                    file: base64ToFile(img.base64, img.name, img.type),
                    base64: img.base64,
                };
                refImages[refType] = imageFile;
            }
        }

        resolved.push(refImages);
    }

    console.log('[ImageGenerators] Resolved character images:', resolved.map(r => ({
        name: r.name,
        hasFront: !!r.front,
        hasProfile: !!r.profile,
        hasBack: !!r.back,
    })));

    return resolved;
}

// === ENGINE IMPLEMENTATIONS ===

/**
 * Generate candidates using Nano Banana Pro (existing)
 */
async function generateWithNanoBananaPro(
    request: ImageGenerationRequest,
    characterImages: CharacterImageRefs[]
): Promise<ImageGenerationResult> {
    try {
        const candidates: ImageFile[] = [];
        const numCandidates = request.shot.image_provider.candidates || 8;

        // Build prompt with camera details
        const basePrompt = composeShotPrompt(request.shot, request.dogma);
        const fullPrompt = request.promptSuffix ? `${basePrompt}\n\n${request.promptSuffix}` : basePrompt;

        // Generate candidates one by one (Nano doesn't support batch)
        for (let i = 0; i < numCandidates; i++) {
            const result = await generateNanoPreview({
                textPrompt: fullPrompt,
                dogma: request.dogma,
                // Inject character reference images
                characters: request.characters,
            });

            if (result.previewImage) {
                candidates.push(result.previewImage);
            }
        }

        return {
            candidates,
            engine: 'nano_banana_pro',
            seed: request.shot.image_provider.seed,
        };
    } catch (error) {
        console.error('[ImageGenerators] Nano Banana Pro error:', error);
        return {
            candidates: [],
            engine: 'nano_banana_pro',
            error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
}

/**
 * Generate candidates using Flux API
 * Flux supports multi-reference image injection for better character consistency
 */
async function generateWithFlux(
    request: ImageGenerationRequest,
    characterImages: CharacterImageRefs[]
): Promise<ImageGenerationResult> {
    try {
        const candidates: ImageFile[] = [];
        const numCandidates = request.shot.image_provider.candidates || 8;

        // Deterministic prompt composition
        const basePrompt = composeShotPrompt(request.shot, request.dogma);
        const fullPrompt = request.promptSuffix ? `${basePrompt}\n\n${request.promptSuffix}` : basePrompt;

        // Prepare reference images for Flux
        const referenceImages = characterImages.flatMap(ci =>
            [ci.front, ci.profile, ci.back].filter(Boolean) as ImageFile[]
        );

        // Add reference image for variations/adapt if provided
        const finalReferenceImages = [...referenceImages];
        if (request.referenceImage) {
            finalReferenceImages.unshift({
                file: base64ToFile(request.referenceImage, 'ref.png', 'image/png'),
                base64: request.referenceImage
            });
        }

        // Call Flux API endpoint
        const response = await fetch('/api/flux/generate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: fullPrompt,
                negative_prompt: request.shot.negative_prompt || '',
                num_images: numCandidates,
                aspect_ratio: request.aspectRatio,
                seed: request.shot.image_provider.seed,
                quality: request.shot.image_provider.quality,
                // Character reference images for identity preservation
                reference_images: referenceImages.map(img => ({
                    base64: img.base64,
                    weight: 0.8, // High weight for identity lock
                })),
                // Dogma style reference
                style_reference: request.dogma?.referenceImages?.[0]?.base64,
            }),
        });

        if (!response.ok) {
            throw new Error(`Flux API error: ${response.status}`);
        }

        const data = await response.json();

        // Convert Flux response to ImageFile[]
        for (const img of data.images || []) {
            candidates.push({
                file: base64ToFile(img.base64, 'flux_candidate.png', 'image/png'),
                base64: img.base64,
            });
        }

        return {
            candidates,
            engine: 'flux',
            seed: data.seed,
        };
    } catch (error) {
        console.error('[ImageGenerators] Flux error:', error);
        return {
            candidates: [],
            engine: 'flux',
            error: error instanceof Error ? error.message : 'Flux API not available',
        };
    }
}

/**
 * Generate candidates using LTX-2 Image API
 */
async function generateWithLTX2Image(
    request: ImageGenerationRequest,
    characterImages: CharacterImageRefs[]
): Promise<ImageGenerationResult> {
    try {
        const candidates: ImageFile[] = [];
        const numCandidates = request.shot.image_provider.candidates || 8;

        const basePrompt = composeShotPrompt(request.shot, request.dogma);
        const fullPrompt = request.promptSuffix ? `${basePrompt}\n\n${request.promptSuffix}` : basePrompt;

        const referenceImages = characterImages.flatMap(ci =>
            [ci.front, ci.profile, ci.back].filter(Boolean) as ImageFile[]
        );

        // Add reference image for variations/adapt if provided
        const finalReferenceImages = [...referenceImages];
        if (request.referenceImage) {
            finalReferenceImages.unshift({
                file: base64ToFile(request.referenceImage, 'ref.png', 'image/png'),
                base64: request.referenceImage
            });
        }

        // Call LTX-2 Image API
        const response = await fetch('/api/ltx/image', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: fullPrompt,
                negative_prompt: request.shot.negative_prompt || '',
                num_images: numCandidates,
                aspect_ratio: request.aspectRatio,
                seed: request.shot.image_provider.seed,
                reference_images: referenceImages.map(img => img.base64),
            }),
        });

        if (!response.ok) {
            throw new Error(`LTX-2 Image API error: ${response.status}`);
        }

        const data = await response.json();

        for (const img of data.images || []) {
            candidates.push({
                file: new File([new Uint8Array()], 'ltx2_candidate.png', { type: 'image/png' }),
                base64: img.base64,
            });
        }

        return {
            candidates,
            engine: 'ltx2_image',
            seed: data.seed,
        };
    } catch (error) {
        console.error('[ImageGenerators] LTX-2 Image error:', error);
        return {
            candidates: [],
            engine: 'ltx2_image',
            error: error instanceof Error ? error.message : 'LTX-2 Image API not available',
        };
    }
}


// === MAIN GENERATOR ===

/**
 * Generate shot candidates using the specified engine
 * Automatically injects character reference images
 */
export async function generateShotCandidates(
    request: ImageGenerationRequest
): Promise<ImageGenerationResult> {
    // Resolve character images FIRST - this is the key fix
    const characterImages = resolveCharacterImages(
        request.shot.characters,
        request.characters
    );

    console.log('[ImageGenerators] Generating candidates', {
        engine: request.shot.image_provider.engine,
        numCandidates: request.shot.image_provider.candidates,
        hasCharacterRefs: characterImages.length > 0,
        hasDogma: !!request.dogma,
    });

    // Route to appropriate engine
    switch (request.shot.image_provider.engine) {
        case 'nano_banana_pro':
            return generateWithNanoBananaPro(request, characterImages);

        case 'flux':
            return generateWithFlux(request, characterImages);

        case 'ltx2_image':
            return generateWithLTX2Image(request, characterImages);



        default:
            console.warn(`[ImageGenerators] Unknown engine: ${request.shot.image_provider.engine}`);
            return generateWithNanoBananaPro(request, characterImages);
    }
}

// === HELPERS ===

/**
 * Build a structured camera prompt from shot camera spec
 */
function buildCameraPrompt(shot: ShotSpec): string {
    const c = shot.camera;
    const parts: string[] = [];

    // Scale
    const scaleMap: Record<string, string> = {
        plan_ensemble: 'extreme wide shot',
        demi_ensemble: 'wide shot',
        plan_large: 'wide shot',
        plan_moyen: 'medium shot',
        plan_americain: 'american shot (knees up)',
        plan_rapproche: 'medium close-up',
        plan_poitrine: 'chest-level close-up',
        plan_epaule: 'over-the-shoulder shot',
        gros_plan: 'close-up',
        tres_gros_plan: 'extreme close-up',
        insert: 'insert detail shot',
    };
    parts.push(scaleMap[c.scale] || c.scale);

    // Angle
    if (c.angle !== 'neutre') {
        const angleMap: Record<string, string> = {
            plongee: 'high angle (looking down)',
            contre_plongee: 'low angle (looking up)',
            dutch: 'dutch angle (tilted)',
        };
        parts.push(angleMap[c.angle] || '');
    }

    // Lens
    if (c.lens_mm) {
        parts.push(`${c.lens_mm}mm lens`);
    }

    // Movement
    if (c.movement !== 'fixe') {
        const movementMap: Record<string, string> = {
            travelling_avant: 'dolly in',
            travelling_arriere: 'dolly out',
            travelling_lateral: 'tracking shot',
            panoramique: 'pan',
            tilt_up: 'tilt up',
            tilt_down: 'tilt down',
            steadicam: 'steadicam smooth movement',
            dolly_zoom: 'dolly zoom (vertigo effect)',
            crane: 'crane shot',
            handheld: 'handheld camera shake',
            arc: 'arc movement around subject',
            orbit: '360 orbit around subject',
        };
        parts.push(movementMap[c.movement] || c.movement);
    }

    // Screen direction
    if (c.screen_direction && c.screen_direction !== 'neutral') {
        parts.push(`subject moving ${c.screen_direction.replace('_', ' ')}`);
    }

    return `[CAMERA: ${parts.join(', ')}]`;
}

// === CHARACTER GENERATOR (Single Image) ===

/**
 * Generate a single character visualization using the specified engine
 */
// === CHARACTER GENERATOR (Registry Aware) ===

/**
 * Generate a single character visualization using the specified engine from Registry
 */
export async function generateCharacterVisual(
    prompt: string,
    contextImages: { file: File; base64: string }[],
    styleImage: ImageFile | null,
    engine: string,
    apiKey?: string
): Promise<ImageFile> {
    console.log('[ImageGenerators] Generate Character Visual', { engine });

    // Look up model in registry
    const model = getModelById(engine);
    const provider = model?.provider || 'gemini';

    // 1. Gemini / Nano Provider
    if (provider === 'gemini') {
        return generateWithNano(prompt, contextImages, styleImage, apiKey, '2:3');
    }

    // 2. Fal Provider (Flux, etc for Images)
    if (provider === 'fal' && model?.type === 'image') {
        try {
            // Pass endpoint to backend
            const endpointOverride = model.endpoint;

            const response = await fetch('/api/flux/generate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    prompt: prompt,
                    width: 768,
                    height: 1024,
                    num_outputs: 1,
                    reference_images: contextImages,
                    fal_endpoint: endpointOverride
                }),
            });

            if (!response.ok) throw new Error(`Fal API Error: ${response.status}`);
            const data = await response.json();

            // Handle Fal response (expecting images array)
            // Flux backend returns objects with url or base64?
            // api/flux/generate.js returns { images: [ { url: ... } ] } or { images: [ base64 ] }?
            // Actually api/flux/generate.js proxies Fal result structure directly if simple, OR processes it.
            // Let's assume standard Fal response: { images: [{ url: "...", width: ..., height: ... }] }
            // OR if fast inference: { images: [{ url: ... }] }
            // Our backend usually unifies this? 
            // Let's handle generic Fal "images" array.

            let b64 = null;
            let file: File | null = null;

            if (data.images && data.images.length > 0) {
                const imgData = data.images[0];
                if (imgData.url) {
                    const imgRes = await fetch(imgData.url);
                    const blob = await imgRes.blob();
                    b64 = await blobToBase64(blob);
                    file = new File([blob], `fal_${engine}.png`, { type: 'image/png' });
                } else if (typeof imgData === 'string') {
                    // base64 string
                    b64 = imgData;
                    // convert to file...
                }
            }

            if (!b64 && data.image?.url) { // Single image format
                const imgRes = await fetch(data.image.url);
                const blob = await imgRes.blob();
                b64 = await blobToBase64(blob);
                file = new File([blob], `fal_${engine}.png`, { type: 'image/png' });
            }

            if (!b64) throw new Error('No image returned from Fal Provider');

            return {
                file: file || new File([new Uint8Array()], `fal_${engine}.png`, { type: 'image/png' }),
                base64: b64
            };

        } catch (e) {
            console.warn(`[ImageGenerators] Fal provider failed for ${engine}, falling back to Nano`, e);
            // Verify if we should fallback or throw. For now fallback is safer for user experience.
            return generateWithNano(prompt, contextImages, styleImage, apiKey, '2:3');
        }
    }

    // Default Fallback
    return generateWithNano(prompt, contextImages, styleImage, apiKey, '2:3');
}

/**
 * Check if an engine is available
 */
export async function checkEngineAvailability(engine: string): Promise<boolean> {
    const model = getModelById(engine);
    return !!model;
}

// Helper
async function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            // result is "data:image/png;base64,....."
            // We want just the base64 part for consistency with some parts of app,
            // BUT ImageFile.base64 usually expects JUST the data.
            // CharacterManager seems to handle it.
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// === DRIVE INTEGRATION ===

/**
 * Generate an image and automatically upload to Drive
 * Returns the image with Drive metadata attached
 */
export async function generateAndUploadImage(
    prompt: string,
    contextImages: { file: File; base64: string }[],
    styleImage: ImageFile | null,
    engine: string,
    options?: {
        apiKey?: string;
        projectId?: string;
        skipDriveUpload?: boolean;
    }
): Promise<ImageFile & { driveFileId?: string }> {
    console.log('[ImageGenerators] Generate and upload image', { engine, skipDriveUpload: options?.skipDriveUpload });

    // 1. Generate the image
    const result = await generateCharacterVisual(
        prompt,
        contextImages,
        styleImage,
        engine,
        options?.apiKey
    );

    // 2. Optionally upload to Drive
    if (!options?.skipDriveUpload && result.file) {
        try {
            // Convert File to Blob for upload
            const blob = result.file as Blob;
            const filename = `gen_${Date.now()}_${engine.replace(/[^a-z0-9]/gi, '_')}.png`;

            const uploadResult = await uploadAssetToDrive(
                blob,
                filename,
                'image',
                {
                    prompt,
                    modelId: engine,
                    projectId: options?.projectId
                }
            );

            if (uploadResult.success && uploadResult.asset) {
                console.log('[ImageGenerators] Image uploaded to Drive:', uploadResult.asset.driveFileId);
                return {
                    ...result,
                    driveFileId: uploadResult.asset.driveFileId
                };
            } else {
                console.warn('[ImageGenerators] Drive upload skipped:', uploadResult.error);
            }
        } catch (err) {
            console.warn('[ImageGenerators] Drive upload failed:', err);
            // Don't fail the generation if upload fails
        }
    }

    return result;
}
